import { Avatar, Box, Button, List, ListItem, Typography, IconButton, SvgIcon, Popover, Fade } from "@material-ui/core";
import { CallEnd, Mic, MicOff, PhoneInTalk, Smartphone, Close } from "@material-ui/icons";
import { FC, useState, useEffect, useCallback } from "react";
import { useStopwatch } from "react-timer-hook";

import { useLazySpeakerTalkdownStartQuery } from "@/api/speakerTalkdown/speakerTalkdownApi";
import { useDispatch } from "@/store";
import { addAlert, Severity } from "@/store/alerts/slice";
import { Config, CallControls, startTwilioCall } from "@/utilities/twilio";
import { OpenButton } from "@/utilities/types/streaming";

import useStyles from "./SpeakerTalkdownButton.styles";


interface SipInfo {
    sipPhoneNumber: string | null;
    sipPin: string | null;
    clientPasscode: number | null;
}

interface SpeakerTalkdownButtonProps {
    sipInfo: SipInfo,
    handleMenu: (button: OpenButton) => void;
    liveUnitId: number;
    open: boolean;
}

const SpeakerTalkdownButton: FC<SpeakerTalkdownButtonProps> = ({ sipInfo, liveUnitId, handleMenu, open }) => {
    const [isCallConnected, setIsCallConnected] = useState(false);
    const [isCallConnecting, setIsCallConnecting] = useState(false);
    const [selfMuted, setSelfMuted] = useState(false);
    const [callControls, setCallControls] = useState<CallControls | null>(null);
    // const [unitMuted, setUnitMuted] = useState(true);
    // const [isUnitMuteProcessing, setIsUnitMuteProcessing] = useState(false);
    const [startSpeakerTalkdown, startSpeakerTalkdownState] = useLazySpeakerTalkdownStartQuery(undefined);

    const dispatch = useDispatch();
    const classes = useStyles();
    const { seconds, minutes, start, reset } = useStopwatch({ autoStart: false });

    // Callbacks passed to the SIP handler
    const callConnected = useCallback(() => {
        setIsCallConnected(true);
        setIsCallConnecting(false);
        start(); // start call duration timer
    }, []);
    const callStoppedFailure = useCallback((error: string) => {
        console.log("Call Error", error);
        setIsCallConnected(false);
        setCallControls(null);
        setSelfMuted(false);
        reset(); // reset call duration timer
        // setUnitMuted(true);

        dispatch(
            addAlert({
                severity: Severity.Error,
                message: `Call failed: ${error}`,
            }),
        );
    }, [startSpeakerTalkdownState]);
    const callStoppedSuccess = useCallback(() => {
        setIsCallConnected(false);
        setCallControls(null);
        setSelfMuted(false);
        reset(); // reset call duration timer
        // setUnitMuted(true);
    }, [startSpeakerTalkdownState]);

    // Utilizing Speaker Talkdown endpoints
    const intiateTalkdown = () => {
        startSpeakerTalkdown(liveUnitId);
        setIsCallConnecting(true);
    };

    useEffect(() => {
        const { data, isSuccess, isFetching } = startSpeakerTalkdownState;

        if (data && isSuccess && !isFetching) {
            const config: Config = {
                token: data.data.token,
                lvtLiveUnitId: data.data.lvt_live_unit_id,
                callConnected,
                callStoppedFailure,
                callStoppedSuccess,
            };

            // The SIP handler passes up some functions to control the call, saved as callControls
            const callControls = startTwilioCall(config);

            setCallControls(callControls);
        }
    }, [startSpeakerTalkdownState]);

    // Utilizing callControls
    const hangupCall = () => {
        if (callControls) {
            callControls.hangup();
        }

        handleMenu(OpenButton.None);
        setIsCallConnecting(false);
    };
    const muteSelf = () => {
        if (callControls) {
            callControls.muteLocal();
            setSelfMuted(true);
        }
    };
    const unmuteSelf = () => {
        if (callControls) {
            callControls.unmuteLocal();
            setSelfMuted(false);
        }
    };

    return (
        <>
            <Box display="flex" flexDirection="column" alignItems="center">
                <SvgIcon onClick={() => handleMenu(OpenButton.SpeakerTalkDown)} className={classes.buttonIcon}>
                    <PhoneInTalk />
                </SvgIcon>
                <Typography variant="caption">Call Unit</Typography>
            </Box>

            {/* Two popovers are used due to a bug with dynamic content
              * https://github.com/mui/material-ui/issues/21561
              */}
            <Popover
                classes={{ paper: classes.popover }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                open={open && isCallConnecting || isCallConnected}
                TransitionComponent={Fade}
            >
                <div className={classes.callMenu}>
                    <Button
                        variant="contained"
                        className={classes.redButton}
                        onClick={isCallConnected || isCallConnecting ? hangupCall : intiateTalkdown}
                    >
                        <CallEnd />
                        {isCallConnecting ?
                            "Calling..."
                            :
                            <Typography>{minutes.toString().padStart(2, "0")}:{seconds.toString().padStart(2, "0")} End Call</Typography>
                        }
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={selfMuted ? unmuteSelf : muteSelf}
                    >
                        {selfMuted ? <MicOff /> : <Mic />}
                        <Typography>Mute your mic</Typography>
                    </Button>
                </div>
            </Popover>

            <Popover
                classes={{ paper: classes.popover }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                open={open && !isCallConnected && !isCallConnecting}
                TransitionComponent={Fade}
            >
                <div className={classes.talkdownMenu}>
                    <IconButton
                        aria-label="close"
                        onClick={() => handleMenu(OpenButton.None)}
                        className={classes.close}
                    >
                        <Close />
                    </IconButton>
                    <Typography className={classes.header}>Call Unit</Typography>

                    <List>
                        <ListItem disableGutters>
                            <Avatar className={classes.avatarIcon} >
                                <Smartphone />
                            </Avatar>
                            <Box display="flex" flexDirection="column">
                                <Typography>{sipInfo.sipPhoneNumber}</Typography>
                                <Typography>EXT: {sipInfo.sipPin} | Pass: {sipInfo.clientPasscode}</Typography>
                            </Box>
                        </ListItem>
                        <ListItem disableGutters>
                            <Avatar className={classes.avatarIcon}>
                                <PhoneInTalk />
                            </Avatar>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="large"
                                onClick={intiateTalkdown}
                            >
                                Call Through LVT
                            </Button>
                        </ListItem>
                    </List>
                </div>
            </Popover>
        </>
    );
};

export default SpeakerTalkdownButton;
